/**
 * @vitest-environment jsdom
 */

import React from "react";
import { describe, expect, it, vi, afterEach } from "vitest";
import {
  render,
  screen,
  cleanup,
  fireEvent,
  waitFor,
} from "@testing-library/react";

import PlainCodeView from "./PlainCodeView";

afterEach(() => {
  cleanup();
  vi.unstubAllGlobals();
});

vi.mock("../Code", () => ({
  default: ({ snippet }: { snippet: string }) => (
    <div data-testid="code-block">{snippet}</div>
  ),
}));

vi.mock("../Icon", () => ({
  default: ({ name }: { name: string }) => (
    <span data-testid="icon" data-icon={name} />
  ),
}));

vi.mock("./CopyButton", () => ({
  default: ({ onCopy }: { onCopy: () => void }) => (
    <button data-testid="copy-button" onClick={onCopy}>
      Copy
    </button>
  ),
}));

describe("PlainCodeView", () => {
  // The copy control only mounts on hover, so every case has to hover first.
  const copy = () => {
    fireEvent.mouseEnter(screen.getByRole("button", { name: /code view/i }));
    fireEvent.click(screen.getByTestId("copy-button"));
  };

  it("writes the content to the clipboard and reports it", async () => {
    const writeText = vi.fn().mockResolvedValue(undefined);
    vi.stubGlobal("navigator", { clipboard: { writeText } });
    const onCopy = vi.fn();

    render(
      <PlainCodeView
        content="npx @ably/cli init"
        language="shell"
        icon={null}
        onCopy={onCopy}
      />,
    );
    copy();

    expect(writeText).toHaveBeenCalledWith("npx @ably/cli init");
    await waitFor(() => expect(onCopy).toHaveBeenCalledTimes(1));
    expect(onCopy).toHaveBeenCalledWith("npx @ably/cli init");
  });

  it("does not report a copy the clipboard rejected", async () => {
    const writeText = vi.fn().mockRejectedValue(new Error("denied"));
    vi.stubGlobal("navigator", { clipboard: { writeText } });
    const warn = vi.spyOn(console, "warn").mockImplementation(() => {});
    const onCopy = vi.fn();

    render(
      <PlainCodeView
        content="npx @ably/cli init"
        language="shell"
        icon={null}
        onCopy={onCopy}
      />,
    );
    copy();

    await waitFor(() => expect(warn).toHaveBeenCalled());
    expect(onCopy).not.toHaveBeenCalled();
    warn.mockRestore();
  });

  it("still copies when no callback is given", () => {
    const writeText = vi.fn().mockResolvedValue(undefined);
    vi.stubGlobal("navigator", { clipboard: { writeText } });

    render(
      <PlainCodeView
        content="ably channels list"
        language="shell"
        icon={null}
      />,
    );

    copy();

    expect(writeText).toHaveBeenCalledWith("ably channels list");
  });
});
